import { Panel } from 'primereact/panel';
import { useTranslation } from "react-i18next";
import NadaConsta from "../../../../components/nadaConsta";
import { formatarParaMoedaBRL, formatarCPF } from "../../../../utils";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';

export function ProtestoNacionalCpf({
  data,
  isConsultaNova,
}: {
  data: any;
  isConsultaNova?: boolean;
}) {
  const { t } = useTranslation();

  const identificacao = data?.sinteseCadastral;
  const resumo = data?.resumoProtestos;
  const protestos = data?.protestos;
  const hasProtestos = protestos && protestos.length > 0;

  const formatarDataNascimento = (valor?: string) => {
    if (!valor || valor.length < 10) return valor;
    const [ano, mes, dia] = valor.substring(0, 10).split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const getResumos = () => [
    {
      icon: 'pi pi-file',
      label: t('quantidade'),
      value: resumo?.total ?? 0,
    },
    {
      icon: 'pi pi-dollar',
      label: t('valor'),
      value: resumo?.valorTotalNumerico != null
        ? formatarParaMoedaBRL(Number(resumo.valorTotalNumerico))
        : '0',
    },
  ];

  if (isConsultaNova) {
    return (
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
          padding: '24px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                backgroundColor: hasProtestos ? '#FEF2F2' : '#F0FDF4',
                borderRadius: '10px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i
                className={hasProtestos ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'}
                style={{ color: hasProtestos ? '#DC2626' : '#16A34A', fontSize: '18px' }}
              />
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1E3A8A' }}>
              {t('protestoNacionalCpf')}
            </h3>
          </div>
          {data?.decisaoNegociarCliente != null && (
            <span
              style={{
                backgroundColor: data.decisaoNegociarCliente ? '#F0FDF4' : '#FEF2F2',
                color: data.decisaoNegociarCliente ? '#16A34A' : '#DC2626',
                border: `1px solid ${data.decisaoNegociarCliente ? '#BBF7D0' : '#FECACA'}`,
                borderRadius: '20px',
                padding: '4px 14px',
                fontSize: '0.75rem',
                fontWeight: 700,
              }}
            >
              {t('decisaoNegociarCliente')}: {data.decisaoNegociarCliente ? 'Sim' : 'Não'}
            </span>
          )}
        </div>

        {/* Identificação */}
        {identificacao && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '20px',
            }}
          >
            {[
              { label: t('cpf'), value: formatarCPF(identificacao.cpf) },
              { label: t('nome'), value: identificacao.nome },
              { label: t('dataNascimento'), value: formatarDataNascimento(identificacao.dataNascimento) },
              { label: t('nomeMae'), value: identificacao.nomeMae },
              { label: t('condicaoCpf'), value: identificacao.condicaoCpf },
            ].map(f => (
              <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                  {f.label}
                </span>
                <span style={{ fontSize: 'small', color: '#1F2937', fontWeight: 500, wordBreak: 'break-word' }}>
                  {f.value || '—'}
                </span>
              </div>
            ))}
          </div>
        )}

        {!hasProtestos ? (
          <NadaConsta />
        ) : (
          <>
            {/* Summary cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '12px',
                marginBottom: '20px',
              }}
            >
              {getResumos().map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: '#1E3A8A',
                    borderRadius: '12px',
                    padding: '14px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ color: '#93C5FD', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                      {item.label}
                    </span>
                    <span style={{ color: '#FFFFFF', fontWeight: 800, fontSize: '1rem' }}>{item.value}</span>
                  </div>
                  <span
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <i className={item.icon} style={{ color: '#FFFFFF', fontSize: '14px' }} />
                  </span>
                </div>
              ))}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #F3F4F6' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'small' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                    {[t('tipo'), t('cartorio'), t('data'), t('cidade'), t('uf'), t('valor')].map(h => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 14px',
                          fontWeight: 700,
                          color: '#6B7280',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {protestos.map((prot: any, idx: number) => (
                    <tr
                      key={idx}
                      style={{ borderBottom: idx < protestos.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#FEF2F2')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <td style={{ padding: '10px 14px', color: '#374151' }}>{prot.tipo || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#1F2937', fontWeight: 600 }}>{prot.cartorio || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#374151' }}>{prot.data || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#374151' }}>{prot.cidade || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#374151', fontWeight: 600 }}>{prot.uf || '—'}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#DC2626', whiteSpace: 'nowrap' }}>
                        {prot.valorNumerico != null ? formatarParaMoedaBRL(Number(prot.valorNumerico)) : (prot.valor || '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Panel header={t('protestoNacionalCpf')}>
      {identificacao && (
        <div className="grid">
          {[
            { label: t('cpf'), value: formatarCPF(identificacao.cpf) },
            { label: t('nome'), value: identificacao.nome },
            { label: t('dataNascimento'), value: formatarDataNascimento(identificacao.dataNascimento) },
            { label: t('nomeMae'), value: identificacao.nomeMae },
            { label: t('condicaoCpf'), value: identificacao.condicaoCpf },
          ].map(f => (
            <div key={f.label} className="col p-fluid flex flex-wrap">
              <div>
                <label className="label-dados">{f.label}</label>
                <div>{f.value || '—'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <br></br>
      {!hasProtestos ? (
        <NadaConsta />
      ) : (
        <div>
          <div className="flex-consulta flex-wrap gap-3">
            {getResumos().map((item: any, index: number) => (
              <Card className='flex-1' style={{ backgroundColor: '#2e2eb8' }} key={index}>
                <div className="flex-consulta justify-content-between gap-5">
                  <div className="flex-consulta flex-column gap-1">
                    <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
                    <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
                  </div>
                  <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
                    <i className={item.icon} />
                  </span>
                </div>
              </Card>
            ))}
          </div>
          <br></br>
          <DataTable size="small" value={protestos} tableStyle={{ minWidth: '50rem' }}>
            <Column field="tipo" header={t('tipo')}></Column>
            <Column field="cartorio" header={t('cartorio')}></Column>
            <Column field="data" header={t('data')}></Column>
            <Column field="cidade" header={t('cidade')}></Column>
            <Column field="uf" header={t('uf')}></Column>
            <Column field="valor" header={t('valor')}></Column>
          </DataTable>
        </div>
      )}
    </Panel>
  );
}
